/**
 * InvoicesPage — B1.9 InvoiceList smart wrapper (Faz 1.3).
 *
 * Architecture (`.cursor/rules/lovable-boundary.mdc`):
 *   - This page owns data fetching (`useInvoiceList`), tenant scoping
 *     (`useCurrentBranch`), filter state, mutation wiring (issue / send /
 *     void / download), idempotency key derivation, toast surfacing, and
 *     navigation.
 *   - The Lovable-delivered composite (`InvoiceListView`) in
 *     `shared/ui/invoice/` owns all visuals: header, summary cards, filter
 *     bar, tab chips, data grid, detail drawer, send/void dialogs, bulk
 *     action bar.
 *
 * State machine (ADR-105):
 *   draft → issued → sent → paid
 *                 ↘ failed (retry) ↘ voided
 *
 * @see docs/decisions/business/ADR-105-e-invoice-integrator-selection.md
 * @see docs/_context/PETOTA_BUSINESS_LOVABLE_BRIEF_B1_9_INVOICE_LIST.md
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useCurrentBranch } from '../shared/context/TenantContext';
import {
  buildIssueIdempotencyKey,
  DEFAULT_LIST_FILTERS,
  useDownloadInvoice,
  useInvoiceList,
  useIssueInvoice,
  useSendInvoice,
  useVoidInvoice,
  type InvoiceListFilters,
  type InvoiceListSnapshot,
} from '../features/invoice';
import {
  enInvoiceLabels,
  InvoiceListView,
  trInvoiceLabels,
} from '../shared/ui/invoice';

export function InvoicesPage(): JSX.Element {
  const { i18n } = useTranslation(['invoices', 'common']);
  const locale: 'tr' | 'en' = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const labels = locale === 'tr' ? trInvoiceLabels : enInvoiceLabels;
  const navigate = useNavigate();
  const branch = useCurrentBranch();

  const [filters, setFilters] = useState<InvoiceListFilters>(DEFAULT_LIST_FILTERS);
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);

  const list = useInvoiceList(branch?.branchId, filters);
  const issue = useIssueInvoice(branch?.branchId);
  const send = useSendInvoice(branch?.branchId);
  const voidMutation = useVoidInvoice(branch?.branchId);
  const download = useDownloadInvoice();

  const issueAttemptsRef = useRef<Map<string, number>>(new Map());

  const snapshot = useMemo<InvoiceListSnapshot>(() => {
    if (list.data) return list.data;
    return {
      branchId: branch?.branchId ?? '',
      branchName: branch?.branchName ?? '',
      filters,
      totals: {
        totalCount: 0,
        draftCount: 0,
        issuedCount: 0,
        paidCount: 0,
        failedCount: 0,
        grossMinor: 0,
        discountMinor: 0,
        taxMinor: 0,
        netMinor: 0,
        paidMinor: 0,
        balanceMinor: 0,
      },
      rows: [],
      hasMore: false,
    };
  }, [branch?.branchId, branch?.branchName, filters, list.data]);

  const isBusy =
    issue.isPending || send.isPending || voidMutation.isPending || download.isPending;

  const error = list.error
    ? { message: list.error instanceof Error ? list.error.message : labels.errors.loadFailed }
    : null;

  const handleFiltersChange = useCallback((next: InvoiceListFilters) => {
    setFilters(next);
  }, []);

  const handleSelectionChange = useCallback((ids: readonly string[]) => {
    setSelectedIds(ids);
  }, []);

  const handleRowClick = useCallback(() => {
    // Detail drawer opens inside the composite; no navigation needed here.
  }, []);

  const handleIssue = useCallback(
    async (invoiceId: string) => {
      const attempts = issueAttemptsRef.current;
      const next = (attempts.get(invoiceId) ?? 0) + 1;
      attempts.set(invoiceId, next);
      try {
        await issue.mutateAsync({
          invoiceId,
          idempotencyKey: buildIssueIdempotencyKey(invoiceId, next),
        });
        toast.success(labels.toasts.issued);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : labels.errors.issueFailed,
        );
      }
    },
    [issue, labels.errors.issueFailed, labels.toasts.issued],
  );

  const handleSend = useCallback(
    async (
      invoiceId: string,
      channel: 'email' | 'whatsapp',
      target: string,
      note?: string,
    ) => {
      try {
        await send.mutateAsync({ invoiceId, channel, target, note });
        toast.success(labels.toasts.sent);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : labels.errors.sendFailed);
      }
    },
    [labels.errors.sendFailed, labels.toasts.sent, send],
  );

  const handleVoid = useCallback(
    async (invoiceId: string, reason: string, explanation?: string) => {
      try {
        await voidMutation.mutateAsync({ invoiceId, reason, explanation });
        toast.success(labels.toasts.voided);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : labels.errors.voidFailed);
      }
    },
    [labels.errors.voidFailed, labels.toasts.voided, voidMutation],
  );

  const handleDownload = useCallback(
    async (invoiceId: string, format: 'pdf' | 'ubl_xml') => {
      try {
        const res = await download.mutateAsync({ invoiceId, format });
        toast.success(labels.toasts.downloaded);
        if (res.url && res.url !== '#' && typeof window !== 'undefined') {
          window.open(res.url, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : labels.errors.downloadFailed,
        );
      }
    },
    [download, labels.errors.downloadFailed, labels.toasts.downloaded],
  );

  const handleCopyPayLink = useCallback(
    (invoiceId: string) => {
      const row = snapshot.rows.find((r) => r.id === invoiceId);
      const url = row?.shareUrl ?? '';
      if (!url || typeof navigator === 'undefined' || !navigator.clipboard) return;
      navigator.clipboard
        .writeText(url)
        .then(() => toast.success(labels.toasts.linkCopied))
        .catch(() => toast.error(labels.errors.sendFailed));
    },
    [labels.errors.sendFailed, labels.toasts.linkCopied, snapshot.rows],
  );

  const handleBulkAction = useCallback(
    async (
      action: 'send' | 'download' | 'void',
      ids: readonly string[],
    ) => {
      if (ids.length === 0) return;
      if (action === 'download') {
        for (const id of ids) {
          await handleDownload(id, 'pdf');
        }
      } else if (action === 'send') {
        for (const id of ids) {
          const row = snapshot.rows.find((r) => r.id === id);
          const target = row?.customer.email ?? '';
          if (target) await handleSend(id, 'email', target);
        }
      } else if (action === 'void') {
        for (const id of ids) {
          await handleVoid(id, 'bulk_void');
        }
      }
      setSelectedIds([]);
    },
    [handleDownload, handleSend, handleVoid, snapshot.rows],
  );

  const handleCreateManual = useCallback(() => {
    navigate('/cashier');
  }, [navigate]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleRetry = useCallback(() => {
    list.refetch();
  }, [list]);

  return (
    <div className="min-h-full bg-biz-background" data-testid="invoices-page">
      <InvoiceListView
        snapshot={snapshot}
        labels={labels}
        locale={locale}
        isBusy={isBusy}
        isLoading={list.isLoading}
        error={error}
        selectedIds={selectedIds}
        onFiltersChange={handleFiltersChange}
        onSelectionChange={handleSelectionChange}
        onRowClick={handleRowClick}
        onIssue={handleIssue}
        onSend={handleSend}
        onVoid={handleVoid}
        onDownload={handleDownload}
        onCopyPayLink={handleCopyPayLink}
        onBulkAction={handleBulkAction}
        onCreateManual={handleCreateManual}
        onPageChange={handlePageChange}
        onRetry={handleRetry}
      />
    </div>
  );
}

export default InvoicesPage;
